/**
 * Brett-Geometrie — alle Flächen-, Rotations- und Kamera-Berechnungen des Playtest-Bretts.
 *
 * Weltkoordinaten sind die des Servers (y wächst nach unten, Weltmittelpunkt (0, 0)).
 * Rotation ist 0 oder 180 (Drehung um den Weltmittelpunkt); wer oben sitzt, sieht das Brett um 180° gedreht.
 * Kamera: { x, y, zoom } — (x, y) ist der Weltpunkt, der in der Mitte des Viewports erscheint.
 * Bildschirmpunkte sind relativ zur Viewport-Mitte (Ursprung = Mitte, y nach unten).
 * Reine Funktionen ohne React und DOM.
 */

/** Kartengröße in Weltkoordinaten (5:7); `card.position` ist der Mittelpunkt der Karte. */
export const CARD_SIZE = { w: 140, h: 195 };

/** Fläche je Teilnehmer in Karten: 12 nebeneinander, 4 übereinander. */
export const AREA_CARDS = { w: 12, h: 4 };
export const AREA_SIZE = { w: CARD_SIZE.w * AREA_CARDS.w, h: CARD_SIZE.h * AREA_CARDS.h };

/** Höchstzahl der Teilnehmer an einem Brett (Server: `PLAYTEST_MAX_PLAYERS`). */
export const MAX_PLAYER_COUNT = 4;

const COLUMNS = { 1: 1, 2: 1, 3: 2, 4: 2 };
const ROWS = { 1: 1, 2: 2, 3: 2, 4: 2 };

/** Welt für die Teilnehmerzahl, zentriert um (0, 0). */
export function getWorld(playerCount) {
  const count = COLUMNS[playerCount] ? playerCount : 1;
  const halfW = (AREA_SIZE.w * COLUMNS[count]) / 2;
  const halfH = (AREA_SIZE.h * ROWS[count]) / 2;
  return { minX: -halfW, maxX: halfW, minY: -halfH, maxY: halfH };
}

const rect = (x, y, w, h) => ({ x, y, w, h });
const { w: AW, h: AH } = AREA_SIZE;
const BOTTOM_HALF = rect(-AW / 2, 0, AW, AH);
const TOP_HALF = rect(-AW / 2, -AH, AW, AH);
const BOTTOM_FULL = rect(-AW, 0, AW * 2, AH);
const BOTTOM_LEFT = rect(-AW, 0, AW, AH);
const BOTTOM_RIGHT = rect(0, 0, AW, AH);
const TOP_LEFT = rect(-AW, -AH, AW, AH);
const TOP_RIGHT = rect(0, -AH, AW, AH);

const LAYOUTS = {
  1: [rect(-AW / 2, -AH / 2, AW, AH)],
  2: [BOTTOM_HALF, TOP_HALF],
  3: [BOTTOM_FULL, TOP_LEFT, TOP_RIGHT],
  4: [BOTTOM_LEFT, TOP_LEFT, TOP_RIGHT, BOTTOM_RIGHT],
};

/** Flächen aller Teilnehmer (Position 0 ist immer unten bzw. unten links). */
export function getAreas(playerCount) {
  const layout = LAYOUTS[playerCount] || LAYOUTS[1];
  return layout.map((r, position) => ({ position, rect: { ...r } }));
}

/** 180, wenn die eigene Fläche in der oberen Hälfte liegt, sonst 0. */
export function getViewRotation(viewerPosition, playerCount) {
  const own = getAreas(playerCount).find((a) => a.position === viewerPosition);
  if (!own) return 0;
  return own.rect.y + own.rect.h <= 0 ? 180 : 0;
}

export function rotateWorldPoint(pt, rotation) {
  return rotation === 180 ? { x: -pt.x, y: -pt.y } : { x: pt.x, y: pt.y };
}

export function worldToScreen(pt, camera, rotation) {
  const p = rotateWorldPoint(pt, rotation);
  const c = rotateWorldPoint(camera, rotation);
  return { x: (p.x - c.x) * camera.zoom, y: (p.y - c.y) * camera.zoom };
}

export function screenToWorld(pt, camera, rotation) {
  const c = rotateWorldPoint(camera, rotation);
  return rotateWorldPoint({ x: pt.x / camera.zoom + c.x, y: pt.y / camera.zoom + c.y }, rotation);
}

/**
 * Heimansicht in unrotierten Weltkoordinaten: ein Fenster in Flächengröße (`AREA_SIZE`), mittig in
 * der eigenen Fläche. Bei drei Teilnehmern ist die Fläche unten doppelt so breit; die ganze Fläche
 * zu zeigen hieße, so weit herauszuzoomen wie die Übersicht.
 */
export function getHomeRect(viewerPosition, playerCount) {
  const own = getAreas(playerCount).find((a) => a.position === viewerPosition) || getAreas(playerCount)[0];
  const { x, y, w, h } = own.rect;
  return rect(x + (w - AW) / 2, y + (h - AH) / 2, AW, AH);
}

/** Vereinigung aller belegten Flächen (die Welt der Teilnehmerzahl). */
export function getOverviewRect(playerCount) {
  const { minX, minY, maxX, maxY } = getWorld(playerCount);
  return rect(minX, minY, maxX - minX, maxY - minY);
}

/** Kleinster Zoom von `fitCamera`: ein Viewport unter zwei Rändern ergäbe sonst Zoom ≤ 0 (Karten ohne Breite). */
export const MIN_FIT_ZOOM = 0.01;

const NO_INSETS = { top: 0, bottom: 0 };

/**
 * Kamera, die das Rechteck mit paddingPx Rand an jeder Seite vollständig zeigt; der Zoom fällt nie
 * unter `MIN_FIT_ZOOM`.
 * `insets` (`{ top, bottom }` in Pixeln) sind Streifen, die Overlays oben und unten verdecken
 * (Teilnehmer-Köpfe, Dock in Ruhe): das Rechteck passt dann in den Bereich dazwischen und steht in
 * dessen Mitte. Erst dieser Versatz hängt von `rotation` ab, denn bei 180° zeigt „oben" im
 * Bildschirm in der Welt nach unten.
 */
export function fitCamera(r, viewport, rotation, paddingPx = 24, insets = NO_INSETS) {
  const top = insets.top || 0;
  const bottom = insets.bottom || 0;
  const zoom = Math.max(MIN_FIT_ZOOM, Math.min(
    (viewport.width - 2 * paddingPx) / r.w,
    (viewport.height - top - bottom - 2 * paddingPx) / r.h
  ));
  const center = { x: r.x + r.w / 2, y: r.y + r.h / 2 };
  return cameraKeepingPoint(center, { x: 0, y: (top - bottom) / 2 }, zoom, rotation);
}

/** Kartenbreite in Pixeln beim gegebenen Zoom. */
export function getCardSizePx(zoom) {
  return CARD_SIZE.w * zoom;
}

export function clampToRect(pt, r) {
  return {
    x: Math.min(Math.max(pt.x, r.x), r.x + r.w),
    y: Math.min(Math.max(pt.y, r.y), r.y + r.h),
  };
}

/** Kamera mit gegebenem Zoom, bei der der Weltpunkt genau auf dem Bildschirmpunkt liegt (Zoom um den Zeiger, Schwenken). */
export function cameraKeepingPoint(worldPt, screenPt, zoom, rotation) {
  const offset = rotateWorldPoint({ x: screenPt.x / zoom, y: screenPt.y / zoom }, rotation);
  return { x: worldPt.x - offset.x, y: worldPt.y - offset.y, zoom };
}

/**
 * Drehung einer Brettkarte, wie der Betrachter sie sieht: Karten liegen zu ihrem Teilnehmer hin
 * ausgerichtet, Karten von der gegenüberliegenden Seite stehen deshalb auf dem Kopf.
 */
export function getCardRotation(holderPosition, viewerPosition, playerCount) {
  return (getViewRotation(holderPosition, playerCount) + getViewRotation(viewerPosition, playerCount)) % 360;
}

/** Gemeinsame Kante zweier Rechtecke als Strecke, sonst null. */
function sharedEdge(a, b) {
  if (a.x + a.w === b.x || b.x + b.w === a.x) {
    const x = a.x + a.w === b.x ? b.x : a.x;
    const y1 = Math.max(a.y, b.y);
    const y2 = Math.min(a.y + a.h, b.y + b.h);
    return y2 > y1 ? { a: { x, y: y1 }, b: { x, y: y2 } } : null;
  }
  if (a.y + a.h === b.y || b.y + b.h === a.y) {
    const y = a.y + a.h === b.y ? b.y : a.y;
    const x1 = Math.max(a.x, b.x);
    const x2 = Math.min(a.x + a.w, b.x + b.w);
    return x2 > x1 ? { a: { x: x1, y }, b: { x: x2, y } } : null;
  }
  return null;
}

/** Trennlinien (Weltkoordinaten) zwischen je zwei aneinandergrenzenden belegten Flächen. */
export function getDividers(playerCount, occupiedPositions) {
  const occupied = getAreas(playerCount).filter((a) => occupiedPositions.includes(a.position));
  const segments = [];
  for (let i = 0; i < occupied.length; i += 1) {
    for (let j = i + 1; j < occupied.length; j += 1) {
      const edge = sharedEdge(occupied[i].rect, occupied[j].rect);
      if (edge) segments.push(edge);
    }
  }
  return segments;
}
